import { ENV } from '@configs/env';
import {
    getDisplayName,
    getBugHeading,
    getBlockerDisplayName,
} from '@hooks/projects/projectHelpers';

export function truncateFileName(name, maxLen = 24) {
    if (!name || typeof name !== 'string' || name.length <= maxLen) return name;
    const extMatch = name.match(/\.[a-z0-9]+$/i);
    const ext = extMatch ? extMatch[0] : '';
    const base = ext ? name.slice(0, -ext.length) : name;
    const keep = maxLen - ext.length - 5;
    if (keep < 1) return name.slice(0, maxLen - 3) + '...';
    return base.slice(0, keep) + '.....' + ext;
}

export function resolveAttachmentUrl(url) {
    if (!url || typeof url !== 'string') return url;
    if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) return url;
    if (url.startsWith('mailto:') || url.startsWith('tel:') || url.startsWith('#')) return url;
    if (url.startsWith('//') && url.length > 2 && url[2] !== '/') {
        if (typeof window !== 'undefined') return `${window.location.protocol}${url}`;
        return `https:${url}`;
    }
    try {
        const origin = new URL(ENV.API_URL || '').origin;
        return url.startsWith('/') ? `${origin}${url}` : url;
    } catch {
        return url;
    }
}

function isFileAttachment(att) {
    if (!att) return false;
    return !!(
        att.fileUrl ||
        att.path ||
        att.filename ||
        att.type === 'file' ||
        att.mimeType ||
        (typeof att.size === 'number' && att.size >= 0)
    );
}

function looksLikeFileAttachment(att) {
    if (!att) return false;
    const name = att.name ?? att.title ?? '';
    const hasFileExtension = /\.[a-z0-9]{2,5}$/i.test(name);
    return !!(att.fileUrl ?? att.path ?? att.filename ?? att.mimeType ?? (hasFileExtension && att.url));
}

function getFileAttachmentsForPlaceholders(html, attachments) {
    if (!html || !Array.isArray(attachments) || attachments.length === 0) return [];
    let fileAttachments = attachments.filter(isFileAttachment);
    const placeholderIndices = [...(html.matchAll(/#attachment-(\d+)/g) || [])].map((m) => parseInt(m[1], 10));
    const maxFileIndex = placeholderIndices.length ? Math.max(...placeholderIndices) : -1;
    if (fileAttachments.length === 0 && maxFileIndex >= 0) {
        const fileCount = maxFileIndex + 1;
        const fromEnd = attachments.slice(-fileCount);
        const fromStart = attachments.slice(0, fileCount);
        const getUrls = (arr) => arr.map((a) => a.url ?? a.fileUrl ?? a.path ?? '');
        const urlsFromEnd = getUrls(fromEnd);
        const urlsFromStart = getUrls(fromStart);
        const uniqueFromEnd = new Set(urlsFromEnd).size === fileCount && urlsFromEnd.every(Boolean);
        const uniqueFromStart = new Set(urlsFromStart).size === fileCount && urlsFromStart.every(Boolean);
        const fileScore = (arr) => arr.filter(looksLikeFileAttachment).length;
        if (uniqueFromStart && uniqueFromEnd) {
            fileAttachments = fileScore(fromStart) >= fileScore(fromEnd) ? fromStart : fromEnd;
        } else if (uniqueFromStart) {
            fileAttachments = fromStart;
        } else if (uniqueFromEnd) {
            fileAttachments = fromEnd;
        } else {
            fileAttachments = fromEnd;
        }
    }
    return fileAttachments;
}

export function resolveAttachmentPlaceholders(html, attachments) {
    const fileAttachments = getFileAttachmentsForPlaceholders(html, attachments);
    if (fileAttachments.length === 0) return html || '';
    let out = html;
    fileAttachments.forEach((att, i) => {
        const url = att.url ?? att.fileUrl ?? att.path ?? '';
        if (url) out = out.replace(new RegExp(`#attachment-${i}`, 'g'), url);
    });
    return out;
}

/** Merge API mentions (may be { id } only) with full user list so we have name/email for display. */
export function mergeMentionsWithUsers(mentions, users) {
    const userMap = new Map((Array.isArray(users) ? users : []).map((u) => [String(u.id), u]));
    const list = Array.isArray(mentions) ? mentions : [];
    return list.map((m) => {
        const id = m?.id ?? m;
        const full = userMap.get(String(id)) ?? m;
        return { id: full?.id ?? id, name: full?.name, email: full?.email };
    });
}

/** Replace {{user:id}}, {{milestone:id}}, {{sprint:id}}, {{task:id}}, {{bug:id}}, {{blocker:id}} in API content with link HTML. */
export function resolveMentionPlaceholders(html, opts = {}) {
    if (!html || typeof html !== 'string') return html;
    const mentions = Array.isArray(opts) ? opts : (opts.mentions ?? []);
    const milestones = Array.isArray(opts) ? [] : (opts.milestones ?? []);
    const sprints = Array.isArray(opts) ? [] : (opts.sprints ?? []);
    const tasks = Array.isArray(opts) ? [] : (opts.tasks ?? []);
    const bugs = Array.isArray(opts) ? [] : (opts.bugs ?? []);
    const blockers = Array.isArray(opts) ? [] : (opts.blockers ?? []);
    const userMap = new Map((Array.isArray(mentions) ? mentions : []).map((m) => [String(m.id), m]));
    const milestoneMap = new Map((Array.isArray(milestones) ? milestones : []).map((m) => [String(m.id), m]));
    const sprintMap = new Map((Array.isArray(sprints) ? sprints : []).map((s) => [String(s.id), s]));
    const taskMap = new Map((Array.isArray(tasks) ? tasks : []).map((t) => [String(t.id), t]));
    const bugMap = new Map((Array.isArray(bugs) ? bugs : []).map((b) => [String(b.id), b]));
    const blockerMap = new Map((Array.isArray(blockers) ? blockers : []).map((b) => [String(b.id), b]));
    const escape = (s) => String(s).replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    let out = html;
    out = out.replace(/\{\{user:([^}]+)\}\}/g, (_, id) => {
        const user = userMap.get(id.trim());
        const displayName = user ? getDisplayName(user) : 'User';
        return `<a href="#mention-user-${id.trim()}" data-mention="user" data-username="${escape(displayName)}" contenteditable="false">@${escape(displayName)}</a>`;
    });
    out = out.replace(/\{\{milestone:([^}]+)\}\}/g, (_, id) => {
        const m = milestoneMap.get(id.trim());
        const displayName = m?.name || 'Milestone';
        return `<a href="#mention-milestone-${id.trim()}" data-mention="milestone" data-name="${escape(displayName)}" contenteditable="false">${escape(displayName)}</a>`;
    });
    out = out.replace(/\{\{sprint:([^}]+)\}\}/g, (_, id) => {
        const s = sprintMap.get(id.trim());
        const displayName = s?.name || 'Sprint';
        return `<a href="#mention-sprint-${id.trim()}" data-mention="sprint" data-name="${escape(displayName)}" contenteditable="false">${escape(displayName)}</a>`;
    });
    out = out.replace(/\{\{task:([^}]+)\}\}/g, (_, id) => {
        const t = taskMap.get(id.trim());
        const displayName = t?.name ?? t?.title ?? 'Task';
        return `<a href="#mention-task-${id.trim()}" data-mention="task" data-name="${escape(displayName)}" contenteditable="false">${escape(displayName)}</a>`;
    });
    out = out.replace(/\{\{bug:([^}]+)\}\}/g, (_, id) => {
        const b = bugMap.get(id.trim());
        const displayName = b ? getBugHeading(b, 48) : 'Bug';
        return `<a href="#mention-bug-${id.trim()}" data-mention="bug" data-name="${escape(displayName)}" contenteditable="false">${escape(displayName)}</a>`;
    });
    out = out.replace(/\{\{blocker:([^}]+)\}\}/g, (_, id) => {
        const b = blockerMap.get(id.trim());
        const displayName = b ? getBlockerDisplayName(b, 48) : 'Blocker';
        return `<a href="#mention-blocker-${id.trim()}" data-mention="blocker" data-name="${escape(displayName)}" contenteditable="false">${escape(displayName)}</a>`;
    });
    return out;
}

export function readFileAsDataUrl(file) {
    return new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result);
        r.onerror = reject;
        r.readAsDataURL(file);
    });
}

export function initialsFromName(name) {
    if (!name) return '?';
    return name
        .split(/\s+/)
        .map((s) => s[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
}

